import { Tag, Typography } from 'antd'
import { memo } from 'react'
import { MdOutlineFlag } from 'react-icons/md'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { StatusTag } from '@/Ship/Components/StatusTag'

import { STATUS_MAP } from '../../Contracts/TaskStatus'

import type { TaskTreeNode as TaskTreeNodeContract } from '../../Contracts/Task.types'

const { Text } = Typography

export interface TaskTreeNodeProps {
    item: TaskTreeNodeContract
    /** uuid поточної (відкритої) задачі — для підсвічування. */
    currentUuid?: string | undefined
}

/**
 * Вузол дерева задач (SPEC-008 §5.4): №+назва, статус-бейдж, прапорець
 * пріоритету. Поточна задача підсвічена, видалені — приглушені з позначкою
 * «Видалено» (їх лишає бек, щоб діти лишались досяжними — work order 09 §2).
 * Клік по вузлу відкриває картку задачі через `OpenResourceCardTask`
 * (resource 'Task') — той самий механізм, що й тап по дзвіночку (NotificationDrawer).
 *
 * Компонент презентаційний: розкладку, полотно і точки з'єднання дає
 * Ship-дерево (`Ship/Components/Tree`), сюди приходить лише доменний вузол.
 */
export const TaskTreeNode = memo(({ item, currentUuid }: TaskTreeNodeProps) => {
    const { uuid, number, title, status, isPriority, isRemoved } = item

    const isCurrent = !!currentUuid && currentUuid === uuid
    const statusInfo = status ? STATUS_MAP[status] : undefined

    const openCard = (): void => {
        if (uuid) {
            OpenResourceCardTask({ resource: 'Task', primaryFieldValue: uuid })
        }
    }

    return (
        <div
            onClick={openCard}
            style={{
                width: 240,
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 8,
                cursor: 'pointer',
                background: 'var(--ant-color-bg-container)',
                border: isCurrent ? '2px solid var(--ant-color-primary, #1677ff)' : '1px solid var(--wl-dark-border, #e5e5e5)',
                boxShadow: isCurrent
                    ? '0 0 0 3px rgba(22,119,255,0.12)'
                    : '0 1px 2px rgba(0,0,0,0.06), 0 1px 6px -1px rgba(0,0,0,0.04)',
                opacity: isRemoved ? 0.55 : 1,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Text strong style={{ color: 'var(--ant-color-text-tertiary)', fontSize: 12 }}>
                    №{number}
                </Text>
                {isPriority && <MdOutlineFlag color="#D96857" title="Пріоритет" />}
                {isRemoved && <Tag color="default">Видалено</Tag>}
            </div>

            <Text
                style={{
                    display: 'block',
                    fontWeight: 500,
                    lineHeight: 1.25,
                    marginBottom: 6,
                    textDecoration: isRemoved ? 'line-through' : 'none',
                }}
                ellipsis={{ tooltip: title }}
            >
                {title || '—'}
            </Text>

            <StatusTag label={statusInfo?.label || String(status ?? '—')} color={statusInfo?.color} />
        </div>
    )
})

TaskTreeNode.displayName = 'TaskTreeNode'
